import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Link, useParams } from "react-router-dom";
import { getProgramById } from "../../app/api";
import FloatingShapes from "../common/FloatingShapes";
import ProgramFeedbackForm from "./program-feedback";

const HTMLTextView = ({ htmlContent }: any) => {
  if (!htmlContent) return null;
  return (
    <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
  );
};

export interface IProgram {
  id?: string;
  title?: string;
  description?: string;
  content?: string;
  thumb?: string;
  images?: string[];
  keywords?: string;
  category?: string;
  kind?: string;
  theme?: string;
  guideline?: string;
  materials?: string[];
  environment?: string;
  knowledge?: string;
  ageGroups?: string[];
  durationMinutes?: number;
  attachments?: string[];
  downloadable?: boolean;
  createdAt?: string;
}

const proseClass = "text-neutral-700 leading-[1.8] text-base sm:text-lg [&>p]:mb-5 [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-neutral-900 [&>h2]:mt-10 [&>h2]:mb-3 [&>h3]:text-xl [&>h3]:font-bold [&>h3]:mt-8 [&>h3]:mb-2 [&>img]:rounded-sm [&>img]:my-8 [&>ul]:my-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:my-4 [&>ol]:list-decimal [&>ol]:pl-6 [&>li]:mb-1";

export const ProgramDetail = () => {
  const { t } = useTranslation();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [program, setProgram] = useState<IProgram>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    getProgramById(params.id).then((res: any) => {
      if (cancelled) return;
      if (res && res.data) {
        setProgram(res.data);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setNotFound(true);
      setLoading(false);
    });

    window.document.body.scrollTop = 0;
    window.document.documentElement.scrollTop = 0;

    return () => { cancelled = true; };
  }, [params.id]);

  const hasMeta = !!(program.durationMinutes || (program.ageGroups && program.ageGroups.length) || program.environment);
  // Хоосон мөрүүдийг шүүнэ — админ талаас хоосон утга орох тохиолдол бий.
  const gallery = (program.images || []).filter((src) => !!src);
  const materials = (program.materials || []).filter((m) => !!m);
  const attachments = (program.attachments || []).filter((a) => !!a);

  return (
    <>
      {loading && (
        <div className="w-full h-full fixed top-0 left-0 bg-white/95 z-50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-800 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Dark hero header */}
      <div className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-secondary-500/15 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-accent-500/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="default" />
        <div className="relative pt-40 pb-12 sm:pt-48 sm:pb-16">
          <div className="mx-auto max-w-[1120px] px-6 lg:px-10">
            <Link to="/programs" className="text-sm text-slate-300 hover:text-white transition-colors">
              ← {t('programDetail.back')}
            </Link>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {program.category && (
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-secondary-300 backdrop-blur-sm">
                  {program.category}
                </span>
              )}
              {program.kind && (
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300 backdrop-blur-sm">
                  {program.kind}
                </span>
              )}
              {program.theme && (
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300 backdrop-blur-sm">
                  {program.theme}
                </span>
              )}
            </div>
            <h1 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white">
              {program.title}
            </h1>
            {program.description && (
              <p className="mt-5 text-lg text-slate-300 max-w-3xl leading-relaxed">
                {program.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {notFound && !loading && (
        <div className="bg-white py-24 text-center">
          <p className="text-neutral-500">{t('programDetail.notFound')}</p>
          <Link to="/programs" className="mt-4 inline-block text-primary-600 font-semibold">
            {t('programDetail.back')}
          </Link>
        </div>
      )}

      {!notFound && (
        <div className="bg-white pb-16">
          <div className="mx-auto max-w-[1120px] px-6 lg:px-10">

            {/* Cover image */}
            {program.thumb && (
              <div className="relative -mt-6 mb-10">
                <div className="relative rounded-xl overflow-hidden border border-neutral-200 shadow-xl">
                  <img src={program.thumb} alt={program.title} className="w-full object-cover" />
                </div>
              </div>
            )}

            {/* Meta strip */}
            {hasMeta && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                {program.durationMinutes ? (
                  <div className="border border-neutral-200 rounded-xl p-4">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">{t('programDetail.duration')}</div>
                    <div className="mt-1 font-bold text-neutral-900">{program.durationMinutes} {t('programs.minutes')}</div>
                  </div>
                ) : null}
                {program.ageGroups && program.ageGroups.length > 0 && (
                  <div className="border border-neutral-200 rounded-xl p-4">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">{t('programDetail.ageGroups')}</div>
                    <div className="mt-1 font-bold text-neutral-900">{program.ageGroups.join(', ')}</div>
                  </div>
                )}
                {program.environment && (
                  <div className="border border-neutral-200 rounded-xl p-4">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">{t('programDetail.environment')}</div>
                    <div className="mt-1 font-bold text-neutral-900">{program.environment}</div>
                  </div>
                )}
              </div>
            )}

            {/* Materials */}
            {materials.length > 0 && (
              <section className="mb-10">
                <h2 className="text-2xl font-extrabold text-neutral-900 mb-3">{t('programDetail.materials')}</h2>
                <ul className="list-disc pl-6 text-neutral-700 leading-relaxed">
                  {materials.map((m, i) => <li key={i} className="mb-1">{m}</li>)}
                </ul>
              </section>
            )}

            {/* Guideline */}
            {program.guideline && (
              <section className="mb-10">
                <h2 className="text-2xl font-extrabold text-neutral-900 mb-3">{t('programDetail.guideline')}</h2>
                <div className={proseClass}>
                  <HTMLTextView htmlContent={program.guideline} />
                </div>
              </section>
            )}

            {/* Knowledge */}
            {program.knowledge && (
              <section className="mb-10">
                <h2 className="text-2xl font-extrabold text-neutral-900 mb-3">{t('programDetail.knowledge')}</h2>
                <div className={proseClass}>
                  <HTMLTextView htmlContent={program.knowledge} />
                </div>
              </section>
            )}

            {/* Content */}
            {program.content && (
              <section className="mb-10">
                <h2 className="text-2xl font-extrabold text-neutral-900 mb-3">{t('programDetail.aboutProgram')}</h2>
                <div className={proseClass}>
                  <HTMLTextView htmlContent={program.content} />
                </div>
              </section>
            )}

            {/* Gallery */}
            {gallery.length > 0 && (
              <section className="mb-10">
                <h2 className="text-2xl font-extrabold text-neutral-900 mb-4">{t('programDetail.gallery')}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {gallery.map((src, i) => (
                    <div key={i} className="aspect-[4/3] overflow-hidden rounded-xl bg-neutral-100">
                      <img src={src} alt={`${program.title} ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Attachments — зөвхөн татаж авахыг зөвшөөрсөн үед */}
            {program.downloadable && attachments.length > 0 && (
              <section>
                <h2 className="text-2xl font-extrabold text-neutral-900 mb-3">{t('programDetail.attachments')}</h2>
                <ul className="space-y-2">
                  {attachments.map((url, i) => (
                    <li key={i}>
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        className="text-primary-600 font-semibold hover:underline">
                        📄 {url.split('/').pop()}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Санал өгөх — урсгалын D эгнээ */}
            <ProgramFeedbackForm programId={program.id} />
          </div>
        </div>
      )}
    </>
  );
};

export default ProgramDetail;
